import { useEffect, useState } from 'react';
import LeaderboardService from '../services/LeaderboardService';
import { AURA_CONFIG } from '../components/systems/AuraSystem';
import { onAuthReset } from '../utils/auth';

/**
 * Aura-Regeln der eigenen Schule (Level-Schwellen, Limits, Belohnungen) -
 * einmal geladen, dann fuer alle Anzeigen gemerkt.
 *
 * Bis September 2026 lud jede Seite die Regeln selbst (Aura-Seite, Konto-Seite),
 * und die Profilkarten rechneten immer mit den Standardwerten. Eine Schule mit
 * eigenen Level-Grenzen sah dadurch auf der Karte "noch 300 AP bis Level 3" und
 * auf der Aura-Seite etwas anderes.
 *
 * Analogie: ein Aushang im Lehrerzimmer statt fuenf Kopien in fuenf Klassenraeumen.
 *
 * Verhalten:
 * - Erster Aufruf holt GET /api/v1/aura/config, alle weiteren nutzen den Cache.
 * - Schlaegt der Abruf fehl, bleiben die Standardwerte aus AuraSystem.js.
 * - Bis zur Antwort werden ebenfalls die Standardwerte geliefert (kein Spinner).
 * - Logout oder Nutzerwechsel leeren den Cache (onAuthReset in utils/auth).
 *
 * @returns {{ config: Object, levelThresholds: Object, isLoaded: boolean }}
 */

let cachedConfig = null;
let inflight = null;
// Zaehlt jeden Reset hoch. Eine Antwort, die erst nach Logout/Nutzerwechsel eintrifft,
// gehoert noch zum vorigen Konto und darf den geleerten Cache nicht wieder fuellen.
let generation = 0;

const loadAuraConfig = () => {
  if (cachedConfig) return Promise.resolve(cachedConfig);
  if (!inflight) {
    const requestGeneration = generation;
    inflight = Promise.resolve()
      .then(() => LeaderboardService.fetchAuraConfig?.())
      .then((config) => {
        if (requestGeneration !== generation) return null;
        if (config && typeof config === 'object') {
          cachedConfig = { ...AURA_CONFIG, ...config };
        }
        return cachedConfig;
      })
      .catch(() => null)
      .finally(() => {
        if (requestGeneration === generation) inflight = null;
      });
  }
  return inflight;
};

/** Cache leeren (Logout, Nutzer-/Schulwechsel, Tests). */
export const resetAuraConfigCache = () => {
  generation += 1;
  cachedConfig = null;
  inflight = null;
};

// Logout, abgelaufene Sitzung oder anderes Konto im selben Tab: die naechste Anzeige
// laedt die Regeln der dann angemeldeten Schule neu (Issue #477).
onAuthReset(resetAuraConfigCache);

export function useAuraConfig() {
  const [config, setConfig] = useState(() => cachedConfig || AURA_CONFIG);
  const [isLoaded, setIsLoaded] = useState(() => Boolean(cachedConfig));

  useEffect(() => {
    if (cachedConfig) {
      setConfig(cachedConfig);
      setIsLoaded(true);
      return undefined;
    }

    let active = true;
    loadAuraConfig().then((loaded) => {
      if (!active) return;
      if (loaded) setConfig(loaded);
      setIsLoaded(true);
    });

    return () => {
      active = false;
    };
  }, []);

  return {
    config,
    levelThresholds: config.levelThresholds || AURA_CONFIG.levelThresholds,
    isLoaded,
  };
}

export default useAuraConfig;
